import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { ReportShell, Stat, Table, money } from "./ReportShell";

const RANGES = [
  { key: "all", label: "All", test: () => true },
  { key: "1-2", label: "1–2", test: (n) => n >= 1 && n <= 2 },
  { key: "3-5", label: "3–5", test: (n) => n >= 3 && n <= 5 },
  { key: "6-10", label: "6–10", test: (n) => n >= 6 && n <= 10 },
  { key: "10+", label: "10+", test: (n) => n > 10 },
];

export const StockOnHandReport = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState("");
  const [range, setRange] = useState("all");

  const generate = async (b = brand) => {
    setBusy(true);
    try {
      const { data } = await api.get("/inventory/report", { params: b ? { brand: b } : {} });
      setData(data);
    } finally {
      setBusy(false);
    }
  };

  const pickBrand = (b) => {
    setBrand(b);
    if (data) generate(b);
  };

  const allRows = data?.rows || [];
  const rangeTest = (RANGES.find((r) => r.key === range) || RANGES[0]).test;
  const rows = useMemo(
    () => allRows.filter((r) => rangeTest(r.slab_count)),
    [allRows, range],
  );
  const max = Math.max(1, ...rows.map((r) => r.total_sqft));
  const brands = data?.brands || [];

  const t = rows.length
    ? {
        colors: rows.length,
        slab_value: rows.reduce((s, r) => s + r.slab_value, 0),
        slab_sqft: Math.round(rows.reduce((s, r) => s + r.slab_sqft, 0) * 10) / 10,
        remnant_sqft: Math.round(rows.reduce((s, r) => s + r.remnant_sqft, 0) * 10) / 10,
      }
    : null;

  const csvRows = rows.map((r) => ({
    color: r.slab_color,
    brands: (r.brands || []).join(" / "),
    slabs: r.slab_count,
    slab_sqft: r.slab_sqft,
    remnant_sqft: r.remnant_sqft,
    total_sqft: r.total_sqft,
    value: r.slab_value,
  }));

  return (
    <ReportShell
      id="stock"
      title="Stock On Hand"
      blurb="Square footage and value by color — full slabs plus remaining remnant area."
      onGenerate={() => generate()}
      busy={busy}
      generated={!!data}
      csvRows={csvRows}
      csvName="stock-on-hand.csv"
    >
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-zinc-800 bg-zinc-900/60 px-4 py-3">
        <label className="flex items-center gap-2 text-xs font-semibold text-zinc-300">
          Brand
          <select
            value={brand}
            onChange={(e) => pickBrand(e.target.value)}
            data-testid="stock-brand-filter"
            className="h-9 rounded-md border border-zinc-700 bg-zinc-950 px-3 text-xs text-zinc-100"
          >
            <option value="">All brands</option>
            {brands.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-2 text-xs font-semibold text-zinc-300">
          Slabs
          <div className="flex overflow-hidden rounded-md border border-zinc-700">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                data-testid={`stock-range-${r.key}`}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1.5 ${range === r.key ? "bg-indigo-500/30 text-white" : "text-zinc-400 hover:text-white"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {t && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Colors" value={t.colors} testId="report-stat-colors" />
          <Stat label="Stock Value" value={money(t.slab_value)} tone="text-yellow-400" testId="report-stat-stock-value" />
          <Stat label="Slab Sq Ft" value={t.slab_sqft} tone="text-emerald-400" testId="report-stat-slab-sq-ft" />
          <Stat label="Remnant Sq Ft" value={t.remnant_sqft} tone="text-blue-400" testId="report-stat-remnant-sq-ft" />
        </div>
      )}
      <div className="mt-4">
        <Table
          testId="report-rows"
          rowKey={(r) => r.slab_color}
          rows={rows}
          empty="Nothing matches these filters. Adjust the brand or slab range."
          columns={[
            {
              key: "slab_color",
              label: "Color",
              render: (r) => (
                <div className="min-w-[180px]" data-testid={`report-row-${r.slab_color}`}>
                  <Link
                    to={`/inventory/slabs?q=${encodeURIComponent(r.slab_color)}`}
                    data-testid={`report-color-link-${r.slab_color}`}
                    className="group inline-flex items-center gap-1 font-semibold text-zinc-100 transition-colors hover:text-yellow-400"
                  >
                    <span className="truncate">{r.slab_color}</span>
                    <CaretRight size={12} className="opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>
                  {r.brands?.length > 0 && (
                    <div className="mt-0.5 truncate text-[11px] text-zinc-500">{r.brands.join(" · ")}</div>
                  )}
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-zinc-800">
                    <div className="h-full rounded-full bg-yellow-500" style={{ width: `${(r.total_sqft / max) * 100}%` }} />
                  </div>
                </div>
              ),
            },
            { key: "slab_count", label: "Slabs", right: true },
            { key: "slab_sqft", label: "Slab ft²", right: true },
            { key: "remnant_sqft", label: "Remnant ft²", right: true, render: (r) => <span className="text-blue-400">{r.remnant_sqft}</span> },
            { key: "total_sqft", label: "Total ft²", right: true },
            { key: "slab_value", label: "Value", right: true, render: (r) => <span className="font-bold text-yellow-400">{money(r.slab_value)}</span> },
          ]}
        />
      </div>
    </ReportShell>
  );
};
